import React, { useState } from "react";
import useApi, { doFetch } from "storybook-dashboard/utils/fetching";
import Im from "immutable";

import IndicatorRow from "AppSrc/project/indicators/equations";
import { confirmDelete, alertSuccess } from "traec-react/utils/sweetalert";

import { MetricTargetSet } from "AppSrc/dashboards/targetSet";
import { useCompanyPermissions } from "storybook-dashboard/utils/hooks";
import { Modal, useModal } from "storybook-dashboard/components/modal";

// Delete indicator from current level only
const deleteIndicator = (e, companyId, indicator, triggerIndicators) => {
  e.preventDefault();
  let indicatorId = indicator.get("uid");
  let indicatorName = indicator.getIn(["metric", "name"]);

  confirmDelete({
    text: `This will delete this indicator ${indicatorName}.  Are you sure you would like to proceed?`,
    onConfirm: () => {
      doFetch(`/api/company/${companyId}/indicator/${indicatorId}/`, "DELETE").then(() => {
        triggerIndicators();
      });
    },
  });
};

// Delete indicator from current level and all children
const deleteIndicatorFromTree = (e, companyId, indicator) => {
  e.preventDefault();
  let indicatorId = indicator.get("uid");
  let indicatorName = indicator.getIn(["metric", "name"]);

  confirmDelete({
    text: `This will delete this indicator ${indicatorName} from here and all child business units, projects and reporting packages.  Are you sure you would like to proceed?`,
    onConfirm: () => {
      doFetch(`/api/company/${companyId}/dispatch/`, "POST", {
        type: "DELETE_INDICATOR_FROM_ALL",
        payload: { indicatorId },
      }).then((response) => {
        if (response.ok) {
          alertSuccess({
            text: `Sucessfully removed indicator from this level and all children.`,
            onConfirm: () => {
              location.reload();
            },
          });
        }
      });
    },
  });
};

// Push indicator to all children
const pushToChildren = (e, companyId, indicator) => {
  e.preventDefault();
  let indicatorId = indicator.get("uid");
  let indicatorName = indicator.getIn(["metric", "name"]);

  alertSuccess({
    iconType: "warning",
    title: "Adding to children",
    text: `This will add the indicator ${indicatorName} to all child business units, projects and reporting packages`,
    onConfirm: () => {
      doFetch(`/api/company/${companyId}/dispatch/`, "POST", {
        type: "ADD_INDICATOR_TO_ALL",
        payload: { indicatorId },
      }).then((response) => {
        if (response.ok) {
          alertSuccess({
            text: `Sucessfully added indicator to all children.`,
            onConfirm: () => {
              location.reload();
            },
          });
        }
      });
    },
  });
};

// Push target to all children
const pushTargetToChildren = (e, companyId, indicator) => {
  e.preventDefault();
  let indicatorId = indicator.get("uid");
  let indicatorName = indicator.getIn(["metric", "name"]) || "";
  let metricTargetId = indicator.getIn(["metricTarget", "uid"]) || null;

  alertSuccess({
    iconType: "warning",
    title: "Adding to children",
    text: `This will add the target for indicator ${indicatorName} to all child business units, projects and reporting packages`,
    onConfirm: () => {
      doFetch(`/api/company/${companyId}/dispatch/`, "POST", {
        type: "ADD_TARGET_TO_ALL",
        payload: { indicatorId, metricTargetId },
      }).then((response) => {
        if (response.ok) {
          alertSuccess({
            text: `Sucessfully added target for indicator ${indicatorName} to all children.`,
            onConfirm: () => {
              location.reload();
            },
          });
        }
      });
    },
  });
};

// Delete target from current level only
const deleteTarget = (e, companyId, indicator, triggerTargets) => {
  e.preventDefault();
  let indicatorName = indicator.getIn(["metric", "name"]) || "";
  let metricTargetId = indicator.getIn(["metricTarget", "uid"]) || null;
  if (!metricTargetId) {
    return null;
  }

  confirmDelete({
    text: `This will delete the target set for indicator ${indicatorName}.  Are you sure you would like to proceed?`,
    onConfirm: () => {
      doFetch(`/api/company/${companyId}/target/${metricTargetId}/`, "DELETE").then(() => {
        triggerTargets();
      });
    },
  });
};

// Delete target from current level and all children
const deleteTargetFromTree = (e, companyId, indicator) => {
  e.preventDefault();
  let indicatorId = indicator.get("uid");
  let indicatorName = indicator.getIn(["metric", "name"]);
  let metricTargetId = indicator.getIn(["metricTarget", "uid"]) || null;

  confirmDelete({
    text: `This will delete the target for indicator ${indicatorName} from here and all child business units, projects and reporting packages.  Are you sure you would like to proceed?`,
    onConfirm: () => {
      doFetch(`/api/company/${companyId}/dispatch/`, "POST", {
        type: "DELETE_TARGET_FROM_ALL",
        payload: { indicatorId, metricTargetId },
      }).then((response) => {
        if (response.ok) {
          alertSuccess({
            text: `Sucessfully removed target for indicator from this level and all children.`,
            onConfirm: () => {
              location.reload();
            },
          });
        }
      });
    },
  });
};

// Generate dropdown menu items for indicator operations
const dropDownLinks = (
  companyId,
  indicator,
  showSetTargetForm,
  toggleGreenBelow,
  triggerIndicators,
  triggerTargets
) => {
  let metricTarget = indicator.get("metricTarget");
  let raThreshold = metricTarget ? metricTarget.getIn(["meta_json", "thresholdLow"]) : null;
  let greenBelow = metricTarget ? metricTarget.getIn(["meta_json", "greenBelow"]) : null;

  let items = [
    { name: "Set Targets", onClick: showSetTargetForm },
    {
      name: "Set Cumulative Target",
      linkTo: `/company/${companyId}/indicator/${indicator.get("uid")}/target`,
    },
  ];
  if (metricTarget && !raThreshold) {
    let name = greenBelow ? "Set Green Above Target" : "Set Green Below Target";
    items.push({ name: name, onClick: toggleGreenBelow });
  }

  items.push({});
  items.push({ name: "Add indicator to children", onClick: (e) => pushToChildren(e, companyId, indicator) });
  items.push({
    name: "Delete indicator from here only",
    onClick: (e) => deleteIndicator(e, companyId, indicator, triggerIndicators),
  });
  items.push({
    name: "Delete indicator from here and children",
    onClick: (e) => deleteIndicatorFromTree(e, companyId, indicator),
  });

  if (indicator.get("metricTarget")) {
    items.push({});
    items.push({ name: "Add target to children", onClick: (e) => pushTargetToChildren(e, companyId, indicator) });
    items.push({
      name: "Delete target from here only",
      onClick: (e) => deleteTarget(e, companyId, indicator, triggerTargets),
    });
    items.push({
      name: "Delete target from here and children",
      onClick: (e) => deleteTargetFromTree(e, companyId, indicator),
    });
  }

  return items;
};

function SetTargetModal({ indicator, companyId }) {
  const { hideModal } = useModal();
  const { trigger: triggerTargets } = useApi("/api/company/{companyId}/target/", { companyId });

  return (
    <Modal title="Set Metric Target" onClose={hideModal}>
      <MetricTargetSet
        header={" "}
        indicator={indicator}
        companyId={companyId}
        closeButtonHandler={hideModal}
        postSave={() => {
          triggerTargets();
          hideModal();
        }}
      />
    </Modal>
  );
}

function CompanyIndicatorRow({ companyId, indicator, baseMetrics }) {
  const { setModal } = useModal();
  const { trigger: triggerIndicators } = useApi("/api/company/{companyId}/indicator/", { companyId });
  const { trigger: triggerTargets } = useApi("/api/company/{companyId}/target/", { companyId });

  /**********************
   MENU OPERATIONS
   **********************/

  const toggleGreenBelow = (e) => {
    e.preventDefault();
    let metricTarget = indicator.get("metricTarget");
    if (!metricTarget) {
      return null;
    }
    let metricTargetId = metricTarget.get("uid") || null;
    let greenBelow = metricTarget ? metricTarget.getIn(["meta_json", "greenBelow"]) : null;

    doFetch(`/api/company/${companyId}/target/${metricTargetId}/`, "PATCH", {
      meta_json: { greenBelow: !greenBelow },
    }).then(() => {
      triggerTargets();
    });
  };

  const showSetTargetForm = (e) => {
    setModal(<SetTargetModal indicator={indicator} companyId={companyId} />);
  };

  /**********************
   * RENDER METHODS
   **********************/

  // Merge metrics from indicator with global list
  const indicatorBaseMetrics = (indicator.get("baseMetrics") || Im.List()).reduce((acc, item) => {
    return acc.set(item.get("uid"), item);
  }, Im.Map());
  let _baseMetrics = (baseMetrics || Im.Map()).merge(indicatorBaseMetrics);

  return (
    <IndicatorRowWrapper
      companyId={companyId}
      baseMetrics={_baseMetrics}
      indicator={indicator}
      dropDownLinks={dropDownLinks(
        companyId,
        indicator,
        showSetTargetForm,
        toggleGreenBelow,
        triggerIndicators,
        triggerTargets
      )}
      setTargetComponent={showSetTargetForm}
    />
  );
}

export default CompanyIndicatorRow;

// Wrapper to handle company permissions
function IndicatorRowWrapper(props) {
  let { companyId, dropDownLinks } = props;
  let { isAdmin } = useCompanyPermissions(companyId);
  let _props = {
    ...props,
    dropDownLinks: isAdmin ? dropDownLinks : [],
  };

  return <IndicatorRow {..._props} />;
}
